import { Trans } from '@i18n';
import { Checkbox, FormControlLabel, FormGroup, Stack } from '@mui/material';
import { energy_certificate_type_enum } from '@predium/enums';
import { translateEnergyCertificateTypeEnum } from '@predium/i18n/client';
import isEqual from 'lodash/isEqual';
import React, { memo, useCallback } from 'react';
import Collapsible from '../../common/Collapsible';
import { useBuildingFilters } from '../BuildingFiltersProvider';

type Props = {
  title: React.ReactNode;
};

const EnergyCertificateTypeFilter = function EnergyCertificateTypeFilter({ title }: Props) {
  const { filters, setFilters } = useBuildingFilters();

  const selectedTypes = filters.energyCertificateTypes;
  const noneSelected = filters.noEnergyCertificate?.includes(true) ?? false;

  const handleTypeSelect = useCallback(
    (value: energy_certificate_type_enum, isChecked: boolean) => {
      setFilters((prev) => {
        const prevValues = prev.energyCertificateTypes ?? [];
        const nextValues = isChecked ? [...prevValues, value] : prevValues.filter((it) => it !== value);
        return {
          ...prev,
          energyCertificateTypes: nextValues.length === 0 ? null : nextValues,
          noEnergyCertificate: isChecked ? null : prev.noEnergyCertificate,
        };
      });
    },
    [setFilters],
  );

  const handleNoneSelect = useCallback(
    (isChecked: boolean) => {
      setFilters((prev) => ({
        ...prev,
        noEnergyCertificate: isChecked ? [true] : null,
        energyCertificateTypes: isChecked ? null : prev.energyCertificateTypes,
      }));
    },
    [setFilters],
  );

  return (
    <Collapsible title={title}>
      <Stack spacing={1} mt={-1}>
        <FormGroup
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 1,
            width: '100%',
          }}
        >
          {Object.values(energy_certificate_type_enum).map((item) => (
            <FormControlLabel
              key={item}
              control={
                <Checkbox
                  checked={selectedTypes?.some((it) => it === item) ?? false}
                  onChange={(e) => handleTypeSelect(item, e.target.checked)}
                />
              }
              value={item}
              label={translateEnergyCertificateTypeEnum(item)}
            />
          ))}
          <FormControlLabel
            control={<Checkbox checked={noneSelected} onChange={(e) => handleNoneSelect(e.target.checked)} />}
            label={<Trans i18nKey="DataCollection_BuildingListFilter-noEnergyCertificate" />}
          />
        </FormGroup>
      </Stack>
    </Collapsible>
  );
};

export default memo(EnergyCertificateTypeFilter, isEqual);
